import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UpdateFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { BrowserDetectService } from '@app/finxone-web-frontend/app/lib/services/browser-detector/browser-detector.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { WebFrontendConfig } from '@app/finxone-web-frontend/app/lib/services/config-service/config.type';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { redirectUserToDepositStatusPage } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/savings-account-actions/pisp-deposit.utils';
import { waitForFlutterWebView } from '@app/finxone-web-frontend/app/lib/utils/flutter.utils';
import { ActiveDevice } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, firstValueFrom, take, tap } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: 'finxone-web-frontend-gocardless-pisp-deposit-status',
  templateUrl: './gocardless-pisp-deposit-status.component.html',
})
export class GoCardlessPispDepositStatusComponent implements OnInit, OnDestroy {
  public isLoading = false;

  constructor(
    private savingsAccountService: SavingsAccountService,
    private store: Store,
    private deviceDetector: DeviceDetectorService,
    private browserDetectorService: BrowserDetectService,
    private router: Router,
    private configService: ConfigService,
  ) {}

  ngOnInit() {
    this.isLoading = true;
    const formState = this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
    const activeAccountId = this.store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
    // check if the device is ios and using a non-Safari browser
    //because we do not get redirected to app when using any other browser than Safari in iOS device
    const isSafariBrowserOrWebkit = this.browserDetectorService.isSafariBrowserOrWebkit();
    const isIOS = this.browserDetectorService.isIOS();
    if (isIOS && !isSafariBrowserOrWebkit) {
      this.handleNonSafariIOSDevice();
      return;
    }
    this.checkForFlutterWebViewAndCallHandler();
    this.checkPispDepositStatus(formState?.id, activeAccountId);
  }

  ngOnDestroy(): void {
    this.isLoading = false;
  }

  private async handleNonSafariIOSDevice() {
    // Wait for config before proceeding
    const configValues = await firstValueFrom(this.configService.getConfig().pipe(take(1)));
    window.location.href = this.getAppDeepLinkUrl(configValues) + 'pisp-deposit-status';
    return;
  }

  private getAppDeepLinkUrl(configValues: WebFrontendConfig): string {
    if (!configValues.appDeepLinkUrl) {
      //fallback to default deep link url when config doesn't have the value
      const origin = window.location.origin;
      return origin.includes('development') ? 'app-thisbankdevelopment://' : 'app-thisbankprojects://';
    }
    return configValues.appDeepLinkUrl;
  }

  private checkForFlutterWebViewAndCallHandler(): void {
    if (this.deviceDetector.isMobile()) {
      waitForFlutterWebView().then((flutterWebView) => {
        if (flutterWebView === null) {
          //log an error when instance is not found on mobile devices
          console.error('Flutter web view instance not found');
        }
        flutterWebView?.callHandler('redirectionComplete', []);
      });
    }
  }

  private checkPispDepositStatus(requestId: string, accountId: string): void {
    if (!requestId || !accountId) {
      console.error('Request ID or Account ID is missing');
      return;
    }
    this.savingsAccountService
      .checkPispDepositStatus(requestId, accountId)
      .pipe(
        tap((res) => {
          //update form state with the status of the deposit
          this.store.dispatch(new UpdateFormDataAction({ formData: { pispDepositStatus: res.status } }));
          // use util method from action button to redirect the user
          redirectUserToDepositStatusPage(
            res.status,
            this.store,
            this.router,
            this.deviceDetector.deviceType as ActiveDevice,
          );
        }),
        catchError((err) => {
          console.error(err);
          throw err;
        }),
      )
      .subscribe();
  }
}
